/**
 * @typedef {Object} ThemeVariables
 * @property {string} bodyBackground - the background of the entire body - includes images.
 * @property {string} bodyColor -
 * @property {string} buttonDisabled -
 * @property {string} buttonDisabledColor -
 * @property {string} canvasBackground -
 * @property {string} canvasCTA -
 * @property {string} canvasDotsColor -
 * @property {string} cardDropShadow -
 * @property {string} cardDropShadowActive - wider shadow (e.g. when hovering over a comment)
 * @property {string} cardDropShadowMedium -
 * @property {string} chipDropShadow -
 * @property {string} chipDropShadowFilter -
 * @property {string} containerColor -
 * @property {string} containerColor2 -
 * @property {string} contextMenuHover -
 * @property {string} contextMenuText -
 * @property {string} ctaOverlay -
 * @property {string} ctaTextColor -
 * @property {string} dangerColor -
 * @property {string} dangerColorActive -
 * @property {string} dangerColorHover -
 * @property {string} defaultDropShadow -
 * @property {string} divider -
 * @property {string} documentBackground -
 * @property {string} documentBackgroundActive -
 * @property {string} documentBackgroundFocus -
 * @property {string} documentBackgroundHover -
 * @property {string} dropShadow1525 -
 * @property {string} floatingMenuBackground -
 * @property {string} focusOverlayBackground -
 * @property {string} grey0 -
 * @property {string} grey1 -
 * @property {string} grey2 -
 * @property {string} grey3 -
 * @property {string} grey4 - WARNING: not defined in every theme.
 * @property {string} highContrastDropShadow -
 * @property {string} iconBorder -
 * @property {string} iconButtonFill -
 * @property {string} iconButtonOpacity -
 * @property {string} lightDropShadow -
 * @property {string} listElementHover -
 * @property {string} menuColor -
 * @property {string} menuColorActive -
 * @property {string} menuColorActiveLightened -
 * @property {string} menuColorBackground -
 * @property {string} menuColorFullscreen -
 * @property {string} menuColorFullscreenHover -
 * @property {string} menuColorFullscreenOutline -
 * @property {string} menuColorHover -
 * @property {string} menuColorHover2 -
 * @property {string} menuColorPrimaryCTAOverlay -
 * @property {string} menuColorPrimaryCTAOverlayActive -
 * @property {string} menuColorPrimaryCTAOverlayHover -
 * @property {string} menuColorTinted -
 * @property {string} menuColorTinted2 -
 * @property {string} menuColorTinted3 -
 * @property {string} menuColorTransparent -
 * @property {string} menuColorTransparent30 -
 * @property {string} particleend -
 * @property {string} particleStart -
 * @property {string} primaryCATHover -
 * @property {string} primaryCTA -
 * @property {string} primaryCTA50 -
 * @property {string} primaryCTA100 -
 * @property {string} primaryCTA200 -
 * @property {string} primaryCTA300 -
 * @property {string} primaryCTA400 -
 * @property {string} primaryCTA500 -
 * @property {string} primaryCTA600 -
 * @property {string} primaryCTA700 -
 * @property {string} primaryCTA800 -
 * @property {string} primaryCTA900 -
 * @property {string} primaryCTAActive -
 * @property {string} primaryCTABackground -
 * @property {string} primaryCTADivider -
 * @property {string} primaryCTAHighContrast -
 * @property {string} primaryCTALight -
 * @property {string} primaryCTASuperHighContrast -
 * @property {string} primaryCTATranslucent -
 * @property {string} primaryCTATransparent -
 * @property {string} primaryCTATransparent05 -
 * @property {string} primaryCTATransparent10 -
 * @property {string} primaryCTATransparent15 -
 * @property {string} primaryCTATransparent30 -
 * @property {string} primaryCTATransparentOverlay -
 * @property {string} relatedWordsInvert -
 * @property {string} relatedWordsWebkitInvert -
 * @property {string} reorderablListDropShadow -
 * @property {string} selectedElementBackground -
 * @property {string} selectedElementText -
 * @property {string} shimmerBlendMode -
 * @property {string} shimmerOpacity -
 * @property {string} sliderDot -
 * @property {string} sliderDotActive -
 * @property {string} sliderHandleDropShadow -
 * @property {string} sliderRail -
 * @property {string} sliderTrack -
 * @property {string} successColor -
 * @property {string} textHighlight -
 * @property {string} tinyDropShadow -
 * @property {string} toggleBackground -
 * @property {string} toggleBackgroundHover -
 * @property {string} toggleClickTarget -
 * @property {string} warning - Color for information serving as a warning.
 */

/** @type {ThemeVariables} */
export const themeVariables = {
  bodyBackground: "var(--body-background)",
  bodyColor: "var(--body-color)",
  buttonDisabled: "var(--button-disabled)",
  buttonDisabledColor: "var(--button-disabled-color)",
  canvasBackground: "var(--canvas-background)",
  canvasCTA: "var(--canvas-cta)",
  canvasDotsColor: "var(--canvas-dots-color)",
  cardDropShadow: "var(--card-drop-shadow)",
  cardDropShadowActive: "var(--card-drop-shadow-active)",
  cardDropShadowMedium: "var(--card-drop-shadow-medium)",
  chipDropShadow: "var(--chip-drop-shadow)",
  chipDropShadowFilter: "var(--chip-drop-shadow-filter)",
  containerColor: "var(--container-color)",
  containerColor2: "var(--container-color-2)",
  contextMenuHover: "var(--context-menu-hover)",
  contextMenuText: "var(--context-menu-text)",
  ctaOverlay: "var(--cta-overlay)",
  ctaTextColor: "var(--cta-text-color)",
  dangerColor: "var(--danger-color)",
  dangerColorActive: "var(--danger-color-active)",
  dangerColorHover: "var(--danger-color-hover)",
  defaultDropShadow: "var(--default-drop-shadow)",
  divider: "var(--divider)",
  documentBackground: "var(--document-background)",
  documentBackgroundActive: "var(--document-background-active)",
  documentBackgroundFocus: "var(--document-background-focus)",
  documentBackgroundHover: "var(--document-background-hover)",
  dropShadow1525: "var(--drop-shadow-15-25)",
  floatingMenuBackground: "var(--floating-menu-background)",
  focusOverlayBackground: "var(--focus-overlay-background)",
  grey0: "var(--grey-0)",
  grey1: "var(--grey-1)",
  grey2: "var(--grey-2)",
  grey3: "var(--grey-3)",
  grey4: "var(--grey-4)",
  highContrastDropShadow: "var(--high-contrast-drop-shadow)",
  iconBorder: "var(--icon-border)",
  iconButtonFill: "var(--icon-button-fill)",
  iconButtonOpacity: "var(--icon-button-opacity)",
  lightDropShadow: "var(--light-drop-shadow)",
  listElementHover: "var(--list-element-hover)",
  menuColor: "var(--menu-color)",
  menuColorActive: "var(--menu-color-active)",
  menuColorActiveLightened: "var(--menu-color-active-lightened)",
  menuColorBackground: "var(--menu-color-background)",
  menuColorFullscreen: "var(--menu-color-fullscreen)",
  menuColorFullscreenHover: "var(--menu-color-fullscreen-hover)",
  menuColorFullscreenOutline: "var(--menu-color-fullscreen-outline)",
  menuColorHover: "var(--menu-color-hover)",
  menuColorHover2: "var(--menu-color-hover-2)",
  menuColorPrimaryCTAOverlay: "var(--menu-color-primary-cta-overlay)",
  menuColorPrimaryCTAOverlayActive: "var(--menu-color-primary-cta-overlay-active)",
  menuColorPrimaryCTAOverlayHover: "var(--menu-color-primary-cta-overlay-hover)",
  menuColorTinted: "var(--menu-color-tinted)",
  menuColorTinted2: "var(--menu-color-tinted2)",
  menuColorTinted3: "var(--menu-color-tinted3)",
  menuColorTransparent: "var(--menu-color-transparent)",
  menuColorTransparent30: "var(--menu-color-transparent-30)",
  particleend: "var(--particle-end)",
  particleStart: "var(--particle-start)",
  primaryCATHover: "var(--primary-cta-hover)",
  primaryCTA: "var(--primary-cta)",
  primaryCTA50: "var(--primary-cta-50)",
  primaryCTA100: "var(--primary-cta-100)",
  primaryCTA200: "var(--primary-cta-200)",
  primaryCTA300: "var(--primary-cta-300)",
  primaryCTA400: "var(--primary-cta-400)",
  primaryCTA500: "var(--primary-cta-500)",
  primaryCTA600: "var(--primary-cta-600)",
  primaryCTA700: "var(--primary-cta-700)",
  primaryCTA800: "var(--primary-cta-800)",
  primaryCTA900: "var(--primary-cta-900)",
  primaryCTAActive: "var(--primary-cta-active)",
  primaryCTABackground: "var(--primary-cta-background)",
  primaryCTADivider: "var(--primary-cta-divider)",
  primaryCTAHighContrast: "var(--primary-cta-high-contrast)",
  primaryCTALight: "var(--primary-cta-light)",
  primaryCTASuperHighContrast: "var(--primary-cta-super-high-contrast)",
  primaryCTATranslucent: "var(--primary-cta-translucent)",
  primaryCTATransparent: "var(--primary-cta-transparent)",
  primaryCTATransparent05: "var(--primary-cta-transparent-05)",
  primaryCTATransparent10: "var(--primary-cta-transparent-10)",
  primaryCTATransparent15: "var(--primary-cta-transparent-15)",
  primaryCTATransparent30: "var(--primary-cta-transparent-30)",
  primaryCTATransparentOverlay: "var(--primary-cta-transparent-overlay)",
  relatedWordsInvert: "var(--related-words-invert)",
  relatedWordsWebkitInvert: "var(--related-words-webkit-invert)",
  reorderablListDropShadow: "var(--reorderable-list-drop-shadow)",
  selectedElementBackground: "var(--selected-element-background)",
  selectedElementText: "var(--selected-element-text)",
  shimmerBlendMode: "var(--shimmer-blend-mode)",
  shimmerOpacity: "var(--shimmer-opacity)",
  sliderDot: "var(--slider-dot)",
  sliderDotActive: "var(--slider-dot-active)",
  sliderHandleDropShadow: "var(--slider-handle-drop-shadow)",
  sliderRail: "var(--slider-rail)",
  sliderTrack: "var(--slider-track)",
  successColor: "var(--success-color)",
  textHighlight: "var(--text-highlight)",
  tinyDropShadow: "var(--tiny-drop-shadow)",
  toggleBackground: "var(--toggle-background)",
  toggleBackgroundHover: "var(--toggle-background-hover)",
  toggleClickTarget: "var(--toggle-click-target)",
  warning: "var(--warning)",
};
